import { ref, computed, defineAsyncComponent } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useOutfitting } from '@nuxt-common/outfitting/app/composables/useOutfitting';
import { useOutfittingStore } from '@nuxt-common/outfitting/app/stores/useOutfittingStore';
import { useBrand } from '~/composables/useBrand';
import useOutfittingData from '~/composables/useOutfittingData';
import { usePageData } from '~/composables/usePageData';
import useProductImagesCtaActions from './useProductImagesCtaActions';
import { fireLinkTag } from '~/features/analytics/tagManager';
import { preFetchAsyncComponents } from '~/utils/preFetchAsyncComponents';

type CtaItem = {
  id: string;
  customCssClass: string;
  label: string;
  onClickHandler: () => void;
  showArrow: true;
  onMouseEnterHandler: () => void;
  onMouseLeaveHandler: () => void;
};

export default function useOutfittingCta() {
  const { outfittingProps } = useOutfittingData();
  const { featureFlags } = outfittingProps.value;

  const { pdpCtaItemData, pdpOutfitAvailabilityCheck } = useOutfitting();
  const { addCtaItem } = useProductImagesCtaActions();

  const brandFlags = useBrand();
  const store = useOutfittingStore();

  const hasOutfits = computed(() => store.outfitsAvailability || false);

  // Must be set to true once store action to add the CTA item is called.
  const isAdded = ref(false);
  const outfittingItem = ref<CtaItem | null>(null);
  const isHovered = ref(false);

  const preloadComponent = () => {
    if (!isHovered.value) return;

    preFetchAsyncComponents(defineAsyncComponent(() => import('@nuxt-common/outfitting/app/components/features/stylitics/Outfitting')));
  };

  const debouncedPreload = useDebounceFn(preloadComponent, 300);

  const onEnter = () => {
    isHovered.value = true;
    debouncedPreload();
  };

  const onLeave = () => {
    isHovered.value = false;
  };

  function addCta() {
    const itemData = {
      // passing lexical scope for [store] is needed
      ...pdpCtaItemData(
        {
          brandFlags,
          featureFlags,
          fireLinkTag,
        },
        store,
      ),
      customCssClass: 'font-small',
      showArrow: true,
      onMouseEnterHandler: onEnter,
      onMouseLeaveHandler: onLeave,
    };

    if (featureFlags.isImageIndicatorStylingEnabled) {
      outfittingItem.value = itemData;
    } else {
      addCtaItem(itemData);
    }
    isAdded.value = true;
  }

  async function init() {
    const { product } = usePageData();
    const isProductAvailable: boolean = product?.product?.availability?.available || false;

    // help prevent unnecessary API calls going out if product is not available
    if (!isProductAvailable) return;

    // passing lexical scope for [store] is needed
    await pdpOutfitAvailabilityCheck(outfittingProps.value, store);
    if (hasOutfits.value) addCta();
  }

  return {
    init,
    isAdded,
    outfittingItem,
  };
}
